import { useState, useRef } from 'react';
import { Button } from '@img/ui';

interface UploadResult {
  id: string;
  url: string;
  thumbnailUrl: string | null;
  filename: string;
  size: number;
}

/**
 * 🖼️ مكوّن رفع صورة موحّد
 *    يستخدم مع: صور المستخدم، لوقو الجمعية، صور المزاد، الإيصالات...
 */
export function ImageUploader({
  value,
  onChange,
  purpose,
  relatedType,
  relatedId,
  label = 'رفع صورة',
  hint,
  size = 'md',
  shape = 'square',
  className = '',
}: {
  value?: string | null;
  onChange: (url: string | null) => void;
  purpose: string;
  relatedType?: string;
  relatedId?: string;
  label?: string;
  hint?: string;
  size?: 'sm' | 'md' | 'lg';
  shape?: 'square' | 'circle' | 'wide';
  className?: string;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const sizeClasses = {
    sm: 'w-20 h-20',
    md: 'w-32 h-32',
    lg: 'w-48 h-48',
  };
  const wideClass = shape === 'wide' ? 'w-full h-40 md:h-56' : '';

  const handlePick = () => inputRef.current?.click();

  const handleFile = async (file: File) => {
    setErr(''); setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('purpose', purpose);
      if (relatedType) fd.append('relatedType', relatedType);
      if (relatedId) fd.append('relatedId', relatedId);

      const res = await fetch('/api/uploads', { method: 'POST', body: fd, credentials: 'include' });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error ?? 'فشل الرفع');
      const result: UploadResult = json.data;
      onChange(result.url);
    } catch (e) {
      setErr((e as Error).message);
    } finally { setBusy(false); }
  };

  return (
    <div className={className}>
      {label && <label className="block text-sm font-bold text-ink mb-2">{label}</label>}

      <div className={`relative inline-flex ${shape === 'wide' ? 'w-full' : ''}`}>
        <div
          onClick={handlePick}
          className={`${shape === 'wide' ? wideClass : sizeClasses[size]} ${shape === 'circle' ? 'rounded-full' : 'rounded-2xl'}
            border-2 border-dashed transition cursor-pointer overflow-hidden flex items-center justify-center
            ${value ? 'border-gray-200' : 'border-gray-300 hover:border-navy bg-gray-50 hover:bg-gray-100'}`}
        >
          {busy ? (
            <div className="text-gray-500 text-center"><div className="text-2xl mb-1">⟳</div><div className="text-xs">جاري الرفع…</div></div>
          ) : value ? (
            <img src={value} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="text-gray-400 text-center p-2">
              <div className="text-3xl mb-1">📷</div>
              <div className="text-xs">اضغط لرفع صورة</div>
            </div>
          )}
        </div>

        {value && !busy && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onChange(null); }}
            className="absolute -top-2 -left-2 w-7 h-7 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-lg"
            title="حذف"
          >✕</button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }}
      />
      {hint && <div className="text-xs text-gray-500 mt-1">{hint}</div>}
      {err && <div className="text-xs text-red-600 mt-1">⛔ {err}</div>}
    </div>
  );
}

/**
 * 📁 مكوّن رفع ملف عام (PDF/Word/أي ملف)
 */
export function FileUploader({
  value,
  onChange,
  purpose,
  relatedType,
  relatedId,
  label = 'رفع ملف',
  hint,
  accept = '.pdf,.doc,.docx,.xlsx,.xls,.csv,.txt,image/*',
  className = '',
}: {
  value?: string | null;
  onChange: (data: { url: string; filename: string; size: number } | null) => void;
  purpose: string;
  relatedType?: string;
  relatedId?: string;
  label?: string;
  hint?: string;
  accept?: string;
  className?: string;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [filename, setFilename] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    setErr(''); setBusy(true); setFilename(file.name);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('purpose', purpose);
      if (relatedType) fd.append('relatedType', relatedType);
      if (relatedId) fd.append('relatedId', relatedId);
      const res = await fetch('/api/uploads', { method: 'POST', body: fd, credentials: 'include' });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error ?? 'فشل الرفع');
      onChange({ url: json.data.url, filename: file.name, size: file.size });
    } catch (e) {
      setErr((e as Error).message);
      setFilename(null);
    } finally { setBusy(false); }
  };

  return (
    <div className={className}>
      {label && <label className="block text-sm font-bold text-ink mb-2">{label}</label>}

      <div
        onClick={() => inputRef.current?.click()}
        className={`p-4 rounded-2xl border-2 border-dashed cursor-pointer transition ${
          value ? 'border-green-300 bg-green-50' : 'border-gray-300 hover:border-navy bg-gray-50 hover:bg-gray-100'
        }`}
      >
        {busy ? (
          <div className="text-center text-gray-500"><div className="text-2xl mb-1">⟳</div><div className="text-sm">جاري الرفع…</div></div>
        ) : value ? (
          <div className="text-center">
            <div className="text-3xl mb-1">📎</div>
            <div className="text-sm font-bold text-green-700">{filename ?? 'تم الرفع ✓'}</div>
            <a href={value} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-xs text-navy hover:underline">عرض الملف</a>
          </div>
        ) : (
          <div className="text-center text-gray-500">
            <div className="text-3xl mb-1">📁</div>
            <div className="text-sm">اضغط لاختيار ملف</div>
          </div>
        )}
      </div>

      {value && !busy && (
        <button
          type="button"
          onClick={() => { setFilename(null); onChange(null); }}
          className="text-xs text-red-600 hover:underline mt-2"
        >🗑 حذف</button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }}
      />
      {hint && <div className="text-xs text-gray-500 mt-1">{hint}</div>}
      {err && <div className="text-xs text-red-600 mt-1">⛔ {err}</div>}
    </div>
  );
}

/**
 * 🖼️ مكوّن رفع صور متعددة (للعقارات مثلاً)
 */
export function MultiImageUploader({
  values,
  onChange,
  purpose,
  relatedType,
  relatedId,
  max = 10,
  label = 'رفع صور',
  className = '',
}: {
  values: string[];
  onChange: (urls: string[]) => void;
  purpose: string;
  relatedType?: string;
  relatedId?: string;
  max?: number;
  label?: string;
  className?: string;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFiles = async (files: FileList) => {
    setErr(''); setBusy(true);
    try {
      const remaining = max - values.length;
      const take = Array.from(files).slice(0, remaining);
      const fd = new FormData();
      take.forEach((f) => fd.append('files', f));
      fd.append('purpose', purpose);
      if (relatedType) fd.append('relatedType', relatedType);
      if (relatedId) fd.append('relatedId', relatedId);
      const res = await fetch('/api/uploads/multiple', { method: 'POST', body: fd, credentials: 'include' });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error ?? 'فشل الرفع');
      onChange([...values, ...json.data.map((u: { url: string }) => u.url)]);
    } catch (e) {
      setErr((e as Error).message);
    } finally { setBusy(false); }
  };

  return (
    <div className={className}>
      {label && <label className="block text-sm font-bold text-ink mb-2">{label} <span className="text-xs text-gray-500">({values.length}/{max})</span></label>}

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {values.map((url, i) => (
          <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-gray-200">
            <img src={url} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => onChange(values.filter((_, idx) => idx !== i))}
              className="absolute -top-2 -left-2 w-7 h-7 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-lg"
              title="حذف"
            >✕</button>
          </div>
        ))}
        {values.length < max && (
          <div
            onClick={() => inputRef.current?.click()}
            className="aspect-square rounded-xl border-2 border-dashed border-gray-300 hover:border-navy bg-gray-50 hover:bg-gray-100 cursor-pointer transition flex items-center justify-center"
          >
            {busy ? (
              <div className="text-gray-500 text-center"><div className="text-2xl mb-1">⟳</div><div className="text-[10px]">جاري الرفع…</div></div>
            ) : (
              <div className="text-gray-400 text-center"><div className="text-2xl mb-1">+</div><div className="text-[10px]">إضافة</div></div>
            )}
          </div>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => { if (e.target.files?.length) handleFiles(e.target.files); e.target.value = ''; }}
      />
      {err && <div className="text-xs text-red-600 mt-1">⛔ {err}</div>}
    </div>
  );
}
